import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import { AxiosError } from "axios";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment/moment";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useStatAnalyticsGetMoneyFlowQuery } from "@/api/hooks/stat-analytics-hooks";
import { DataOutdatedAlert } from "@/module/service-configs-module/statistics-visualization-module/components/data-outdated-alert";
import { SelectFilterMessage } from "@/module/service-configs-module/statistics-visualization-module/components/select-filter-message";
import { BarAuChart } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/bar-au-chart";
import { BarChart } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/bar-chart";
import SummaryLine from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/line-chart";
import { PieChartCustom } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/pie-chart";
import SummaryTable from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/summary-table";
import { MoneyFlowRequestDto, TechnologyPresenceDto } from "@/shared/api";

export const defaultValues: MoneyFlowRequestDto = {
  period: "DAY",
  game_criteria: {
    room_id: "",
    game_type: "nlh",
    timestamp_period: {
      gte: moment().startOf("month").valueOf(),
      lte: moment().valueOf(),
    },
    game_size: { gte: 2, lte: 12 },
    //hero_count: 1,
    //opponent_count: 10,
    straddle: undefined,
    antes: [],
    big_blinds: [],
    big_blind_usd_range: { gte: 0, lte: 1000 },
    tables_ids: [],
    hero_count: { gte: 1, lte: 12 },
    opponent_count: { gte: 0, lte: 12 },
    non_hero_count: { gte: 0, lte: 12 },
    //technologies_presence: [{ technology_name: "hud", presence_range: { gte: 1, lte: 3 } }],
    technologies_presence: new Array<TechnologyPresenceDto>(),
    no_technology_presence_range: { gte: 0, lte: 12 },
    alliances: [],
    clubs: [],
  },
  hero_criteria: {
    clustering_patterns: [],
    technologies: [],
    //groups: ["xyz_2"],
    groups: [],
    players_ids: [],
    ai_instances: [],
    ai_profiles: [],
    ai_ids: [],
    clubs: [],
  },
  opponent_criteria: {
    technologies: [],
    groups: [],
    players_ids: [],
    ai_instances: [],
    ai_profiles: [],
    ai_ids: [],
    clustering_patterns: [],
    clubs: [],
  },
  sub_opponents_criteria: [],
};

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title
);

type TotalsPageProps = {
  filter: MoneyFlowRequestDto | null;
  isDataOutdated: boolean;
};

export const TotalsPage = ({ filter, isDataOutdated }: TotalsPageProps) => {
  const { t } = useTranslation();
  const isInitialized = useRef(false);
  const {
    data: statAnalyticsResponse,
    isLoading,
    error: moneyFlowError,
    status: moneyFlowStatus,
  } = useStatAnalyticsGetMoneyFlowQuery({
    domain: filter?.game_criteria.room_id || "",
    moneyFlowRequestDto: filter as MoneyFlowRequestDto,
    queryOptions: {
      enabled: !!filter,
    },
  });

  useEffect(() => {
    if (moneyFlowStatus && moneyFlowStatus === "error") {
      const message = (moneyFlowError as unknown as AxiosError<{ message: string }>)?.response
        ?.data.message;
      enqueueSnackbar(`${message?.slice ? message.slice(0, 500) : message}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moneyFlowStatus]);

  const statAnalyticsData = useMemo(() => {
    return statAnalyticsResponse?.data.period_statistics;
  }, [statAnalyticsResponse]);

  useEffect(() => {
    if (statAnalyticsData) {
      isInitialized.current = !!statAnalyticsData;
    }
  }, [statAnalyticsData]);

  return (
    <Grid>
      <Grid container rowSpacing={2} columnSpacing={2} paddingY={2}>
        {filter ? (
          <>
            {isLoading && !isInitialized.current ? (
              <CircularProgress sx={{ margin: "96px auto 32px" }} />
            ) : (
              <>
                <DataOutdatedAlert isDataOutdated={isDataOutdated} />

                <Grid size={{ xs: 12, md: 12 }}>
                  <Card sx={{ height: 920 }}>
                    <CardContent sx={{ height: "80%" }}>
                      <Typography variant="h6" textAlign="center" gutterBottom>
                        {t("pieChart")}
                      </Typography>
                      {isLoading ? (
                        <Preloader />
                      ) : (
                        <PieChartCustom data={statAnalyticsData ?? []} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Card sx={{ height: 600 }}>
                    <CardContent sx={{ height: "80%" }}>
                      <Typography variant="h6" textAlign="center" gutterBottom>
                        {t("loseDynamic")}
                      </Typography>
                      {isLoading ? <Preloader /> : <BarChart data={statAnalyticsData ?? []} />}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Card sx={{ height: 400 }}>
                    <CardContent
                      sx={{ height: "90%", display: "flex", flexDirection: "column" }}
                    >
                      <Typography variant="h6" textAlign="center" gutterBottom>
                        {t("activityDynamic")}
                      </Typography>
                      {isLoading ? (
                        <Preloader />
                      ) : (
                        <BarAuChart data={statAnalyticsData ?? []} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Card sx={{ height: 710 }}>
                    <CardContent
                      sx={{ height: "90%", display: "flex", flexDirection: "column" }}
                    >
                      <Typography variant="h6" textAlign="center" gutterBottom>
                        {t("summaryStatistics")}
                      </Typography>
                      {isLoading ? (
                        <Preloader />
                      ) : (
                        <SummaryTable data={statAnalyticsData ?? []} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <Card sx={{ height: 650 }}>
                    <CardContent
                      sx={{ height: "90%", display: "flex", flexDirection: "column" }}
                    >
                      <Typography variant="h6" textAlign="center" gutterBottom>
                        {t("unrakedWinrateDynamic")}
                      </Typography>
                      {isLoading ? (
                        <Preloader />
                      ) : (
                        <SummaryLine data={statAnalyticsData ?? []} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </>
        ) : (
          <SelectFilterMessage />
        )}
      </Grid>
    </Grid>
  );
};

export const Preloader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
    }}
  >
    <CircularProgress />
  </Box>
);
